import React, { useState, useEffect } from 'react';
import { Save, Upload, RefreshCw, Globe, Palette, Image, Shield, Calendar, Download, Database, Check, AlertTriangle, BarChart3, Tag, Plus, X, Trash2, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../../services/api';
import { useSettings } from '../../contexts/SettingsContext';
import { Button } from '../../components/ui/button';

// Backup Export Section Component
const BackupExportSection = () => {
  const [collectionsInfo, setCollectionsInfo] = useState({});
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  
  // Export options
  const [exportOptions, setExportOptions] = useState({
    users: true,
    leads: true,
    empresas: true,
    deals: true,
    pipelines: true,
    services: true,
    settings: true,
    meetings: false,
    alerts: false,
    activities: false,
    fields: true,
    attachments: false,
    includePasswords: false,
  });
  
  // Import options
  const [importMode, setImportMode] = useState('merge');
  
  const collectionLabels = {
    users: { label: 'Usuarios', icon: '👥', critical: true },
    leads: { label: 'Leads / Contactos', icon: '📋', critical: true },
    empresas: { label: 'Empresas', icon: '🏢', critical: true },
    deals: { label: 'Negocios', icon: '💰', critical: true },
    pipelines: { label: 'Embudos', icon: '📊', critical: true },
    services: { label: 'Servicios', icon: '🛠️', critical: true },
    settings: { label: 'Configuración', icon: '⚙️', critical: true },
    meetings: { label: 'Reuniones', icon: '📅', critical: false },
    alerts: { label: 'Alertas', icon: '🔔', critical: false },
    activities: { label: 'Actividades', icon: '📝', critical: false },
    fields: { label: 'Campos Personalizados', icon: '📄', critical: true },
    attachments: { label: 'Archivos Adjuntos', icon: '📎', critical: false },
  };

  useEffect(() => {
    loadCollectionsInfo();
  }, []);

  const loadCollectionsInfo = async () => {
    setLoading(true);
    try {
      const info = await api.getCollectionsInfo();
      setCollectionsInfo(info);
    } catch (error) {
      console.error('Error loading collections info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportOptionChange = (key, value) => {
    setExportOptions(prev => ({ ...prev, [key]: value }));
  };

  const selectAll = () => {
    const allTrue = {};
    Object.keys(collectionLabels).forEach(key => {
      allTrue[key] = true;
    });
    setExportOptions(prev => ({ ...prev, ...allTrue }));
  };

  const selectCritical = () => {
    const critical = {};
    Object.entries(collectionLabels).forEach(([key, config]) => {
      critical[key] = config.critical;
    });
    setExportOptions(prev => ({ ...prev, ...critical }));
  };

  const deselectAll = () => {
    const allFalse = {};
    Object.keys(collectionLabels).forEach(key => {
      allFalse[key] = false;
    });
    setExportOptions(prev => ({ ...prev, ...allFalse }));
  };

  const handleExport = async () => {
    const selectedCount = Object.entries(exportOptions)
      .filter(([key, value]) => value && key !== 'includePasswords')
      .length;
    
    if (selectedCount === 0) {
      toast.error('Selecciona al menos una colección para exportar');
      return;
    }

    setExporting(true);
    try {
      const backupData = await api.exportBackup(exportOptions);
      
      // Create and download JSON file
      const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const date = new Date().toISOString().split('T')[0];
      a.href = url;
      a.download = `crm-backup-${date}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Backup exportado correctamente');
    } catch (error) {
      toast.error('Error al exportar backup');
      console.error(error);
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImporting(true);
    try {
      const text = await file.text();
      const backupData = JSON.parse(text);
      
      // Validate backup structure
      if (!backupData.data || !backupData.version) {
        toast.error('Archivo de backup inválido');
        return;
      }

      // Add import mode to backup data
      backupData.importMode = importMode;
      
      const result = await api.importBackup(backupData);
      
      // Show results
      const summary = Object.entries(result.results || {})
        .map(([key, info]) => `${collectionLabels[key]?.label || key}: ${info.count || info.inserted || 0}`)
        .join(', ');
      
      toast.success(`Importación completada: ${summary}`);
      
      // Reload collections info
      loadCollectionsInfo();
      
    } catch (error) {
      if (error instanceof SyntaxError) {
        toast.error('El archivo no es un JSON válido');
      } else {
        toast.error('Error al importar backup');
      }
      console.error(error);
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  const totalRecords = Object.values(collectionsInfo).reduce((a, b) => a + (b || 0), 0);

  return (
    <section className="bg-white rounded-xl border border-slate-200 p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-emerald-100 rounded-lg">
          <Database className="w-5 h-5 text-emerald-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Backup y Migración de Datos</h2>
          <p className="text-sm text-slate-500">Exporta e importa datos para migrar a nuevos despliegues</p>
        </div>
      </div>

      {/* Info Banner */}
      <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">¿Para qué sirve esto?</p>
            <p className="mt-1">Cuando hagas un nuevo deploy del CRM, puedes usar el archivo JSON exportado para cargar todos tus datos existentes (leads, empresas, negocios, configuración, etc.) sin perder información.</p>
          </div>
        </div>
      </div>

      {/* Current Data Summary */}
      <div className="mb-6 p-4 bg-slate-50 rounded-lg">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium text-slate-900">Datos actuales en el sistema</h3>
          <button 
            onClick={loadCollectionsInfo}
            className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(collectionLabels).map(([key, config]) => (
            <div key={key} className="flex items-center gap-2 text-sm">
              <span>{config.icon}</span>
              <span className="text-slate-600">{config.label}:</span>
              <span className="font-medium text-slate-900">{collectionsInfo[key] || 0}</span>
            </div>
          ))}
        </div>
        <div className="mt-3 pt-3 border-t border-slate-200">
          <span className="text-sm text-slate-600">Total de registros: </span>
          <span className="font-semibold text-slate-900">{totalRecords.toLocaleString()}</span>
        </div>
      </div>

      {/* Export Section */}
      <div className="mb-6">
        <h3 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
          <Download className="w-4 h-4" />
          Exportar Datos
        </h3>
        
        {/* Quick Select Buttons */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={selectAll}
            className="px-3 py-1.5 text-xs bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-700"
          >
            Seleccionar todo
          </button>
          <button
            onClick={selectCritical}
            className="px-3 py-1.5 text-xs bg-emerald-100 hover:bg-emerald-200 rounded-lg text-emerald-700"
          >
            Solo críticos
          </button>
          <button
            onClick={deselectAll}
            className="px-3 py-1.5 text-xs bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-700"
          >
            Deseleccionar todo
          </button>
        </div>

        {/* Collection Checkboxes */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
          {Object.entries(collectionLabels).map(([key, config]) => (
            <label
              key={key}
              className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                exportOptions[key] 
                  ? 'bg-emerald-50 border-emerald-300' 
                  : 'bg-white border-slate-200 hover:border-slate-300'
              }`}
            >
              <input
                type="checkbox"
                checked={exportOptions[key]}
                onChange={(e) => handleExportOptionChange(key, e.target.checked)}
                className="w-4 h-4 rounded text-emerald-600"
              />
              <span className="text-lg">{config.icon}</span>
              <div className="flex-1">
                <span className="text-sm font-medium text-slate-700">{config.label}</span>
                <span className="text-xs text-slate-500 ml-2">({collectionsInfo[key] || 0})</span>
              </div>
              {config.critical && (
                <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Crítico</span>
              )}
            </label>
          ))}
        </div>

        {/* Include Passwords Option */}
        <label className="flex items-center gap-3 p-3 mb-4 bg-red-50 rounded-lg border border-red-200 cursor-pointer">
          <input
            type="checkbox"
            checked={exportOptions.includePasswords}
            onChange={(e) => handleExportOptionChange('includePasswords', e.target.checked)}
            className="w-4 h-4 rounded text-red-600"
          />
          <div>
            <span className="text-sm font-medium text-red-800">Incluir contraseñas (hasheadas)</span>
            <p className="text-xs text-red-600">Activa esto solo si quieres que los usuarios puedan usar sus mismas contraseñas en el nuevo sistema</p>
          </div>
        </label>

        <button
          onClick={handleExport}
          disabled={exporting}
          className="flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
        >
          <Download className="w-4 h-4" />
          {exporting ? 'Exportando...' : 'Descargar Backup JSON'}
        </button>
      </div>

      {/* Import Section */}
      <div className="pt-6 border-t border-slate-200">
        <h3 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
          <Upload className="w-4 h-4" />
          Importar Datos
        </h3>
        
        {/* Import Mode */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-slate-700 mb-2">Modo de importación:</label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="importMode"
                value="merge"
                checked={importMode === 'merge'}
                onChange={(e) => setImportMode(e.target.value)}
                className="w-4 h-4 text-blue-600"
              />
              <div>
                <span className="text-sm font-medium text-slate-700">Combinar</span>
                <p className="text-xs text-slate-500">Agrega nuevos registros y actualiza existentes (por ID)</p>
              </div>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="importMode"
                value="replace"
                checked={importMode === 'replace'}
                onChange={(e) => setImportMode(e.target.value)}
                className="w-4 h-4 text-red-600"
              />
              <div>
                <span className="text-sm font-medium text-red-700">Reemplazar</span>
                <p className="text-xs text-red-500">⚠️ Elimina datos existentes y los reemplaza completamente</p>
              </div>
            </label>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 px-6 py-3 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-slate-400 hover:bg-slate-50">
            <Upload className="w-4 h-4 text-slate-500" />
            <span className="text-sm text-slate-600">
              {importing ? 'Importando...' : 'Seleccionar archivo JSON'}
            </span>
            <input
              type="file"
              accept=".json"
              onChange={handleImport}
              disabled={importing}
              className="hidden"
            />
          </label>
        </div>

        <p className="mt-3 text-xs text-slate-500">
          Sube un archivo JSON previamente exportado desde otro CRM para cargar los datos.
        </p>
      </div>
    </section>
  );
};


const TAG_COLORS = ['#6366f1', '#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#64748b'];

const LeadTagsSection = () => {
  const [tags, setTags] = useState([]);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#6366f1');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTags();
  }, []);

  const loadTags = async () => {
    try {
      const data = await api.getLeadTags();
      setTags(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!newTagName.trim()) return;
    try {
      const tag = await api.createLeadTag({ name: newTagName.trim(), color: newTagColor });
      setTags([...tags, tag]);
      setNewTagName('');
      toast.success('Etiqueta creada');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al crear etiqueta');
    }
  };

  const handleDelete = async (tagId) => {
    if (!window.confirm('¿Eliminar esta etiqueta? Se removerá de todos los leads.')) return;
    try {
      await api.deleteLeadTag(tagId);
      setTags(tags.filter(t => t.id !== tagId));
      toast.success('Etiqueta eliminada');
    } catch (err) { toast.error('Error al eliminar'); }
  };

  return (
    <section className="bg-white rounded-xl border border-slate-200 p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-indigo-100 rounded-lg">
          <Tag className="w-5 h-5 text-indigo-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Etiquetas de Leads</h2>
          <p className="text-sm text-slate-500">Crea etiquetas para clasificar y filtrar leads. Los usuarios con rol Asistente solo verán leads con sus etiquetas asignadas.</p>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <input
          value={newTagName}
          onChange={(e) => setNewTagName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
          placeholder="Nombre de la etiqueta..."
          className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          data-testid="new-tag-name"
        />
        <div className="flex items-center gap-1">
          {TAG_COLORS.map(c => (
            <button key={c} onClick={() => setNewTagColor(c)}
              className={`w-6 h-6 rounded-full border-2 transition-all ${newTagColor === c ? 'border-slate-900 scale-110' : 'border-transparent'}`}
              style={{ backgroundColor: c }} />
          ))}
        </div>
        <Button onClick={handleCreate} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="create-tag-btn">
          <Plus className="w-4 h-4 mr-1" /> Crear
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-slate-400">Cargando...</p>
      ) : tags.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-4">No hay etiquetas creadas</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {tags.map(tag => (
            <span key={tag.id} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium text-white"
              style={{ backgroundColor: tag.color }}>
              {tag.name}
              <button onClick={() => handleDelete(tag.id)} className="hover:bg-white/20 rounded-full p-0.5">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </section>
  );
};


const VARIABLES_HELP = '[Nombre del Cliente], [Nombre del Comercial], [Email del Cliente], [Telefono del Cliente], [Empresa], [Nombre del Servicio]';

const WhatsAppTemplatesSection = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.getWhatsAppTemplates().then(t => {
      if (t.length === 0) {
        setTemplates([
          { id: 'wt-1', name: 'Primer contacto', message: 'Hola, [Nombre del Cliente]. Buenas tardes. Soy [Nombre del Comercial] de DataLab. Veo que estás interesado en nuestros servicios, ¿cómo podemos ayudarte?' },
          { id: 'wt-2', name: 'Seguimiento', message: 'Hola [Nombre del Cliente], soy [Nombre del Comercial] de DataLab. Quería hacer seguimiento a nuestra conversación anterior. ¿Tienes alguna pregunta o podemos agendar una reunión?' },
          { id: 'wt-3', name: 'Propuesta enviada', message: 'Hola [Nombre del Cliente], soy [Nombre del Comercial] de DataLab. Ya te enviamos la propuesta al correo. ¿Pudiste revisarla? Estoy atento a tus comentarios.' },
          { id: 'wt-4', name: 'Recordatorio reunión', message: 'Hola [Nombre del Cliente], te recuerdo que tenemos reunión programada. ¿Confirmas tu asistencia? Quedo atento.' },
          { id: 'wt-5', name: 'Agradecimiento', message: 'Hola [Nombre del Cliente], soy [Nombre del Comercial] de DataLab. Gracias por confiar en nosotros. Si necesitas algo no dudes en escribirme.' },
        ]);
      } else {
        setTemplates(t);
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.saveWhatsAppTemplates(templates);
      toast.success('Plantillas guardadas');
    } catch { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const addTemplate = () => {
    setTemplates([...templates, { id: `wt-${Date.now()}`, name: '', message: '' }]);
  };

  const removeTemplate = (idx) => {
    setTemplates(templates.filter((_, i) => i !== idx));
  };

  const updateTemplate = (idx, field, value) => {
    const updated = [...templates];
    updated[idx] = { ...updated[idx], [field]: value };
    setTemplates(updated);
  };

  return (
    <section className="bg-white rounded-xl border border-slate-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-green-100 rounded-lg">
            <MessageCircle className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Plantillas WhatsApp</h2>
            <p className="text-sm text-slate-500">Mensajes predeterminados para enviar a los leads por WhatsApp</p>
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-700 text-white">
          <Save className="w-4 h-4 mr-1" /> {saving ? 'Guardando...' : 'Guardar'}
        </Button>
      </div>

      <p className="text-xs text-slate-400 mb-4 bg-slate-50 rounded-lg px-3 py-2">
        Variables disponibles: <code className="text-slate-600">{VARIABLES_HELP}</code>
      </p>

      {loading ? <p className="text-sm text-slate-400">Cargando...</p> : (
        <div className="space-y-4">
          {templates.map((t, i) => (
            <div key={t.id} className="border border-slate-200 rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2">
                <input value={t.name} onChange={(e) => updateTemplate(i, 'name', e.target.value)}
                  placeholder="Nombre de la plantilla"
                  className="flex-1 px-3 py-1.5 border border-slate-200 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-green-500" />
                <button onClick={() => removeTemplate(i)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <textarea value={t.message} onChange={(e) => updateTemplate(i, 'message', e.target.value)}
                rows={3} placeholder="Escribe el mensaje... usa [Nombre del Cliente] para insertar variables"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none" />
            </div>
          ))}
          <button onClick={addTemplate}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-slate-200 rounded-lg text-slate-500 hover:border-green-400 hover:text-green-600 transition-colors">
            <Plus className="w-4 h-4" /> Agregar Plantilla
          </button>
        </div>
      )}
    </section>
  );
};

const AdminSettings = () => {
  const { reloadSettings } = useSettings();
  const [saving, setSaving] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [settings, setSettings] = useState({
    // Branding
    appName: 'DataCRM',
    appTagline: 'Gestión Inteligente',
    logoUrl: '',
    faviconUrl: '',
    
    // Colors
    primaryColor: '#00b30c',
    secondaryColor: '#64f288',
    accentColor: '#71f75f',
    
    // URLs
    baseUrl: '',
    apiUrl: '',
    cdnUrl: '',
    
    // General
    defaultLanguage: 'es',
    timezone: 'America/Bogota',
    dateFormat: 'DD/MM/YYYY',
    currency: 'EUR',
    currencySymbol: '€',
    
    // Features
    enableRegistration: true,
    enableApiAccess: true,
    enableNotifications: true,
    maintenanceMode: false,
    
    // Security / reCAPTCHA
    enableRecaptcha: false,
    recaptchaSiteKey: '',
    recaptchaSecretKey: '',
    
    // Facebook Ads Integration
    facebookAccessToken: '',
    
    // Google Calendar Integration
    googleCalendarClientId: '',
    googleCalendarClientSecret: '',
    googleCalendarRedirectUrl: '',
    
    // Google Ads Integration
    googleAdsDeveloperToken: '',
    googleAdsRefreshToken: '',
    googleAdsCustomerId: '',
    googleAdsLoginCustomerId: '',
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await api.getSettings();
      if (data) {
        setSettings(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.log('Using default settings');
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleRestoreDefaults = async () => {
    // Show confirmation dialog
    const confirmed = window.confirm(
      '⚠️ ADVERTENCIA: Esta acción eliminará TODOS los leads, empresas, negocios, reuniones y actividades actuales.\n\n' +
      'Se crearán únicamente los datos de demostración:\n' +
      '• 1 Empresa Demo (Empresa Demo - NIT 900000000-0)\n' +
      '• 1 Lead Demo (Juan Pérez)\n\n' +
      '¿Está seguro que desea continuar? Esta acción no se puede deshacer.'
    );
    
    if (!confirmed) return;
    
    // Double confirmation for safety
    const doubleConfirmed = window.confirm(
      '🔴 ÚLTIMA CONFIRMACIÓN\n\n' +
      'Todos los datos serán eliminados permanentemente.\n\n' +
      '¿Confirma que desea restaurar a valores por defecto?'
    );
    
    if (!doubleConfirmed) return;
    
    setRestoring(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/restore-defaults`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(`Base de datos restaurada. Eliminados: ${data.results?.deleted?.leads || 0} leads, ${data.results?.deleted?.empresas || 0} empresas, ${data.results?.deleted?.deals || 0} negocios`);
      } else {
        toast.error(data.detail || 'Error al restaurar');
      }
    } catch (error) {
      toast.error('Error al restaurar la base de datos');
      console.error(error);
    } finally {
      setRestoring(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.saveSettings(settings);
      await reloadSettings(); // Reload settings to apply changes globally
      toast.success('Configuración guardada correctamente');
    } catch (error) {
      toast.error('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        handleChange('logoUrl', reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFaviconUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        handleChange('faviconUrl', reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900">Configuración General</h1>
        <p className="text-slate-500 mt-2">Personaliza la apariencia y comportamiento de tu aplicación</p>
      </div>

      <div className="space-y-8">
        {/* Branding */}
        <section className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Image className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Marca e Identidad</h2>
              <p className="text-sm text-slate-500">Logo, nombre y elementos visuales</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Nombre de la Aplicación
              </label>
              <input
                type="text"
                value={settings.appName}
                onChange={(e) => handleChange('appName', e.target.value)}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Eslogan / Tagline
              </label>
              <input
                type="text"
                value={settings.appTagline}
                onChange={(e) => handleChange('appTagline', e.target.value)}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Logo (PNG, SVG recomendado)
              </label>
              <div className="flex items-center gap-4">
                {settings.logoUrl && (
                  <img src={settings.logoUrl} alt="Logo" className="h-12 w-auto" />
                )}
                <label className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50">
                  <Upload className="w-4 h-4 text-slate-500" />
                  <span className="text-sm text-slate-600">Subir Logo</span>
                  <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Favicon (ICO, PNG 32x32)
              </label>
              <div className="flex items-center gap-4">
                {settings.faviconUrl && (
                  <img src={settings.faviconUrl} alt="Favicon" className="h-8 w-8" />
                )}
                <label className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50">
                  <Upload className="w-4 h-4 text-slate-500" />
                  <span className="text-sm text-slate-600">Subir Favicon</span>
                  <input type="file" accept="image/*" onChange={handleFaviconUpload} className="hidden" />
                </label>
              </div>
            </div>
          </div>
        </section>

        {/* Colors */}
        <section className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Palette className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Colores de la Aplicación</h2>
              <p className="text-sm text-slate-500">Personaliza la paleta de colores</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Color Primario
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={settings.primaryColor}
                  onChange={(e) => handleChange('primaryColor', e.target.value)}
                  className="w-12 h-10 rounded cursor-pointer border-0"
                />
                <input
                  type="text"
                  value={settings.primaryColor}
                  onChange={(e) => handleChange('primaryColor', e.target.value)}
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Color Secundario
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={settings.secondaryColor}
                  onChange={(e) => handleChange('secondaryColor', e.target.value)}
                  className="w-12 h-10 rounded cursor-pointer border-0"
                />
                <input
                  type="text"
                  value={settings.secondaryColor}
                  onChange={(e) => handleChange('secondaryColor', e.target.value)}
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Color de Acento
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={settings.accentColor}
                  onChange={(e) => handleChange('accentColor', e.target.value)}
                  className="w-12 h-10 rounded cursor-pointer border-0"
                />
                <input
                  type="text"
                  value={settings.accentColor}
                  onChange={(e) => handleChange('accentColor', e.target.value)}
                  className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                />
              </div>
            </div>
          </div>

          {/* Color Preview */}
          <div className="mt-6 p-4 bg-slate-50 rounded-lg">
            <p className="text-sm text-slate-500 mb-3">Vista previa:</p>
            <div className="flex items-center gap-4">
              <button 
                style={{ backgroundColor: settings.primaryColor }}
                className="px-4 py-2 text-white rounded-lg text-sm font-medium"
              >
                Botón Primario
              </button>
              <button 
                style={{ backgroundColor: settings.secondaryColor }}
                className="px-4 py-2 text-white rounded-lg text-sm font-medium"
              >
                Botón Secundario
              </button>
              <span 
                style={{ color: settings.accentColor }}
                className="font-medium"
              >
                Texto de Acento
              </span>
            </div>
          </div>
        </section>

        {/* URLs */}
        <section className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-green-100 rounded-lg">
              <Globe className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">URLs y Dominios</h2>
              <p className="text-sm text-slate-500">Configuración para instalación en otros dominios</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                URL Base de la Aplicación
              </label>
              <input
                type="url"
                value={settings.baseUrl}
                onChange={(e) => handleChange('baseUrl', e.target.value)}
                placeholder="https://tudominio.com"
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <p className="text-xs text-slate-500 mt-1">URL principal donde estará alojada la aplicación</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                URL del API Backend
              </label>
              <input
                type="url"
                value={settings.apiUrl}
                onChange={(e) => handleChange('apiUrl', e.target.value)}
                placeholder="https://api.tudominio.com"
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <p className="text-xs text-slate-500 mt-1">URL del servidor backend (dejar vacío para usar el mismo dominio con /api)</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                URL del CDN (opcional)
              </label>
              <input
                type="url"
                value={settings.cdnUrl}
                onChange={(e) => handleChange('cdnUrl', e.target.value)}
                placeholder="https://cdn.tudominio.com"
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <p className="text-xs text-slate-500 mt-1">URL para servir archivos estáticos (imágenes, documentos)</p>
            </div>
          </div>
        </section>

        {/* Regional */}
        <section className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Globe className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Configuración Regional</h2>
              <p className="text-sm text-slate-500">Idioma, zona horaria y formato</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Idioma</label>
              <select
                value={settings.defaultLanguage}
                onChange={(e) => handleChange('defaultLanguage', e.target.value)}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="es">Español</option>
                <option value="en">English</option>
                <option value="pt">Português</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Zona Horaria</label>
              <select
                value={settings.timezone}
                onChange={(e) => handleChange('timezone', e.target.value)}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="America/Bogota">Bogotá (GMT-5)</option>
                <option value="America/Mexico_City">Ciudad de México (GMT-6)</option>
                <option value="America/Lima">Lima (GMT-5)</option>
                <option value="America/Santiago">Santiago (GMT-4)</option>
                <option value="Europe/Madrid">Madrid (GMT+1)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Formato Fecha</label>
              <select
                value={settings.dateFormat}
                onChange={(e) => handleChange('dateFormat', e.target.value)}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                <option value="YYYY-MM-DD">YYYY-MM-DD</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Moneda</label>
              <select
                value={settings.currency}
                onChange={(e) => handleChange('currency', e.target.value)}
                className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="EUR">Euro (€)</option>
                <option value="USD">Dólar ($)</option>
                <option value="COP">Peso Colombiano ($)</option>
                <option value="MXN">Peso Mexicano ($)</option>
              </select>
            </div>
          </div>
        </section>

        {/* Security / reCAPTCHA */}
        <section className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-green-100 rounded-lg">
              <Shield className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Seguridad - reCAPTCHA</h2>
              <p className="text-sm text-slate-500">Protección contra bots en el login</p>
            </div>
          </div>

          <div className="space-y-4">
            <label className="flex items-center justify-between p-4 bg-slate-50 rounded-lg cursor-pointer">
              <div>
                <p className="font-medium text-slate-900">Activar reCAPTCHA en Login</p>
                <p className="text-sm text-slate-500">Requiere verificación para iniciar sesión (contraseña y código)</p>
              </div>
              <input
                type="checkbox"
                checked={settings.enableRecaptcha}
                onChange={(e) => handleChange('enableRecaptcha', e.target.checked)}
                className="w-5 h-5 rounded text-green-600"
              />
            </label>

            {settings.enableRecaptcha && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-green-50 rounded-lg border border-green-200">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Site Key (Clave del sitio)
                  </label>
                  <input
                    type="text"
                    value={settings.recaptchaSiteKey}
                    onChange={(e) => handleChange('recaptchaSiteKey', e.target.value)}
                    placeholder="6LcXXXXXXXXXXXXXXXXXXXXXX"
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  <p className="text-xs text-slate-500 mt-1">Clave pública de Google reCAPTCHA v2</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Secret Key (Clave secreta)
                  </label>
                  <input
                    type="password"
                    value={settings.recaptchaSecretKey}
                    onChange={(e) => handleChange('recaptchaSecretKey', e.target.value)}
                    placeholder="6LcXXXXXXXXXXXXXXXXXXXXXX"
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  <p className="text-xs text-slate-500 mt-1">Clave privada para verificación en servidor</p>
                </div>
                <div className="md:col-span-2">
                  <p className="text-sm text-green-700">
                    <strong>¿No tienes claves?</strong> Obtén tus claves en{' '}
                    <a href="https://www.google.com/recaptcha/admin" target="_blank" rel="noopener noreferrer" className="underline">
                      Google reCAPTCHA Admin Console
                    </a>
                  </p>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Facebook Ads Integration */}
        <section className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Globe className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Integración Facebook Ads</h2>
              <p className="text-sm text-slate-500">Configura el token de acceso para Facebook Ads</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Access Token de Facebook
              </label>
              <input
                type="text"
                value={settings.facebookAccessToken || ''}
                onChange={(e) => handleChange('facebookAccessToken', e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                placeholder="EAA8ZBGZCFT4E0BQ..."
              />
              <p className="text-xs text-slate-500 mt-1">Token de acceso para la API de Facebook Marketing</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-sm text-blue-800">
                <strong>¿Cómo obtener el token?</strong> Genera tu Access Token en:{' '}
                <a 
                  href="https://developers.facebook.com/tools/explorer/" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="underline font-medium hover:text-blue-900"
                >
                  Facebook Graph API Explorer
                </a>
              </p>
              <p className="text-xs text-blue-600 mt-2">
                Asegúrate de seleccionar los permisos: <code className="bg-blue-100 px-1 rounded">ads_read</code>, <code className="bg-blue-100 px-1 rounded">ads_management</code>
              </p>
            </div>
          </div>
        </section>

        {/* Lead Tags Management */}
        <LeadTagsSection />

        {/* WhatsApp Templates */}
        <WhatsAppTemplatesSection />

        {/* Google Calendar Integration */}
        <section className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-red-100 rounded-lg">
              <Calendar className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Integración Google Calendar</h2>
              <p className="text-sm text-slate-500">Configura las credenciales OAuth para Google Calendar</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Client ID
              </label>
              <input
                type="text"
                value={settings.googleCalendarClientId || ''}
                onChange={(e) => handleChange('googleCalendarClientId', e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 font-mono text-sm"
                placeholder="xxxxxx.apps.googleusercontent.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Client Secret
              </label>
              <input
                type="password"
                value={settings.googleCalendarClientSecret || ''}
                onChange={(e) => handleChange('googleCalendarClientSecret', e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 font-mono text-sm"
                placeholder="GOCSPX-xxxxxxxxxx"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                URL de Producción (Redirect URL)
              </label>
              <input
                type="url"
                value={settings.googleCalendarRedirectUrl || ''}
                onChange={(e) => handleChange('googleCalendarRedirectUrl', e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 font-mono text-sm"
                placeholder="https://crm.wearedatalab.com"
              />
              <p className="text-xs text-slate-500 mt-1">
                URL base de tu CRM en producción (sin /api al final)
              </p>
            </div>
            <div className="p-4 bg-red-50 rounded-lg border border-red-100">
              <p className="text-sm text-red-800 font-medium mb-2">
                Configuración para producción:
              </p>
              <ol className="text-sm text-red-700 space-y-2 list-decimal list-inside">
                <li>Ve a <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="underline font-medium">Google Cloud Console</a></li>
                <li>Selecciona tu proyecto o crea uno nuevo</li>
                <li>Ve a "Credenciales" → "Crear credenciales" → "ID de cliente OAuth"</li>
                <li>Agrega tu dominio de producción en "URIs de redirección autorizados":
                  <code className="block mt-1 bg-red-100 px-2 py-1 rounded text-xs">{settings.googleCalendarRedirectUrl || 'https://tu-dominio.com'}/api/oauth/calendar/callback</code>
                </li>
                <li>Copia el Client ID y Client Secret aquí</li>
              </ol>
              <p className="text-xs text-red-600 mt-3">
                <strong>Nota:</strong> Cada usuario debe conectar su propia cuenta de Google Calendar desde su perfil.
              </p>
            </div>
          </div>
        </section>

        {/* Google Ads Integration */}
        <section className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-green-100 rounded-lg">
              <BarChart3 className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Integración Google Ads</h2>
              <p className="text-sm text-slate-500">Configura las credenciales para importar datos de campañas de Google Ads</p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Developer Token *
              </label>
              <input
                type="password"
                value={settings.googleAdsDeveloperToken || ''}
                onChange={(e) => handleChange('googleAdsDeveloperToken', e.target.value)}
                placeholder="Tu Developer Token de Google Ads API"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
              <p className="text-xs text-slate-500 mt-1">Obtenlo en Google Ads → Herramientas → Centro de API</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  MCC ID (Login Customer ID)
                </label>
                <input
                  type="text"
                  value={settings.googleAdsLoginCustomerId || ''}
                  onChange={(e) => handleChange('googleAdsLoginCustomerId', e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder="392-643-8476 → 3926438476"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                <p className="text-xs text-slate-500 mt-1">ID de tu cuenta administradora (sin guiones)</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Customer ID (Cuenta a consultar)
                </label>
                <input
                  type="text"
                  value={settings.googleAdsCustomerId || ''}
                  onChange={(e) => handleChange('googleAdsCustomerId', e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder="973-786-4362 → 9737864362"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                <p className="text-xs text-slate-500 mt-1">ID de la cuenta de Ads a consultar (sin guiones)</p>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Refresh Token
              </label>
              <div className="flex gap-2">
                <input
                  type="password"
                  value={settings.googleAdsRefreshToken || ''}
                  onChange={(e) => handleChange('googleAdsRefreshToken', e.target.value)}
                  placeholder="Se genera automáticamente al conectar"
                  className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  readOnly
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={async () => {
                    try {
                      // First save current settings
                      await handleSave();
                      // Then get auth URL
                      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/google-ads/auth/url`, {
                        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                      });
                      const data = await response.json();
                      if (data.authUrl) {
                        window.location.href = data.authUrl;
                      } else {
                        toast.error(data.detail || 'Error al generar URL de autorización');
                      }
                    } catch (error) {
                      toast.error('Error: Configura primero el Client ID de Google Calendar');
                    }
                  }}
                  className="whitespace-nowrap"
                >
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Conectar con Google
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {settings.googleAdsRefreshToken ? '✅ Token configurado' : '⚠️ Haz clic en "Conectar con Google" para autorizar'}
              </p>
            </div>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
              <h4 className="font-medium text-green-800 mb-2">¿Cómo configurar Google Ads?</h4>
              <ol className="text-sm text-green-700 space-y-1 list-decimal list-inside">
                <li>Configura <strong>Google Calendar</strong> arriba (Client ID y Secret) - se reutilizan para Ads</li>
                <li>Obtén el <strong>Developer Token</strong> en Google Ads → Herramientas → Centro de API</li>
                <li>Ingresa el <strong>MCC ID</strong> y <strong>Customer ID</strong> de tus cuentas</li>
                <li>Haz clic en <strong>"Conectar con Google"</strong> para autorizar el acceso</li>
                <li>Guarda los cambios</li>
              </ol>
            </div>
          </div>
        </section>

        {/* Backup / Export */}
        <BackupExportSection />

        {/* Features */}
        <section className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Shield className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Funcionalidades</h2>
              <p className="text-sm text-slate-500">Activa o desactiva características</p>
            </div>
          </div>

          <div className="space-y-4">
            <label className="flex items-center justify-between p-4 bg-slate-50 rounded-lg cursor-pointer">
              <div>
                <p className="font-medium text-slate-900">Permitir Registro de Usuarios</p>
                <p className="text-sm text-slate-500">Los nuevos usuarios pueden crear cuentas</p>
              </div>
              <input
                type="checkbox"
                checked={settings.enableRegistration}
                onChange={(e) => handleChange('enableRegistration', e.target.checked)}
                className="w-5 h-5 rounded text-indigo-600"
              />
            </label>

            <label className="flex items-center justify-between p-4 bg-slate-50 rounded-lg cursor-pointer">
              <div>
                <p className="font-medium text-slate-900">Acceso a API</p>
                <p className="text-sm text-slate-500">Permitir acceso externo a la API</p>
              </div>
              <input
                type="checkbox"
                checked={settings.enableApiAccess}
                onChange={(e) => handleChange('enableApiAccess', e.target.checked)}
                className="w-5 h-5 rounded text-indigo-600"
              />
            </label>

            <label className="flex items-center justify-between p-4 bg-slate-50 rounded-lg cursor-pointer">
              <div>
                <p className="font-medium text-slate-900">Notificaciones</p>
                <p className="text-sm text-slate-500">Enviar notificaciones push y email</p>
              </div>
              <input
                type="checkbox"
                checked={settings.enableNotifications}
                onChange={(e) => handleChange('enableNotifications', e.target.checked)}
                className="w-5 h-5 rounded text-indigo-600"
              />
            </label>

            <label className="flex items-center justify-between p-4 bg-red-50 rounded-lg cursor-pointer border border-red-200">
              <div>
                <p className="font-medium text-red-900">Modo Mantenimiento</p>
                <p className="text-sm text-red-600">Desactiva el acceso público temporalmente</p>
              </div>
              <input
                type="checkbox"
                checked={settings.maintenanceMode}
                onChange={(e) => handleChange('maintenanceMode', e.target.checked)}
                className="w-5 h-5 rounded text-red-600"
              />
            </label>
          </div>
        </section>

        {/* Save Button */}
        <div className="flex justify-between">
          <button
            onClick={handleRestoreDefaults}
            disabled={restoring}
            className="flex items-center gap-2 px-6 py-3 border border-red-300 rounded-lg text-red-700 hover:bg-red-50"
          >
            <RefreshCw className={`w-4 h-4 ${restoring ? 'animate-spin' : ''}`} />
            {restoring ? 'Restaurando...' : 'Restaurar Leads y Empresas'}
          </button>
          <div className="flex gap-4">
            <button
              onClick={loadSettings}
              className="flex items-center gap-2 px-6 py-3 border border-slate-200 rounded-lg text-slate-700 hover:bg-slate-50"
            >
              <RefreshCw className="w-4 h-4" />
              Recargar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Guardando...' : 'Guardar Configuración'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
